import React, { useEffect, useMemo, useState } from 'react';
import { useProject } from '../ProjectContext';
import { flattenTree, getProjectId } from '@shared/targetHelpers';
import { addTier, removeTier, addValueAtPath, removeValueAtPath, addPackage, removePackage } from '@shared/tierTree';
import { fetchFullProject, saveProject } from '../services/api';
import { Button, Card, Chips, Loading, ErrorText, errorMessage } from '../components/ui';
import { useMe } from '../hooks/useMe';

// Values at one depth under a given ancestor path. Matched by nodeId rather
// than by value, because this screen edits specific nodes - two branches can
// legitimately hold values with the same name.
function optionsAtDepth(tierPaths, depth, ancestorPath) {
  const seen = new Map();
  for (const tp of tierPaths) {
    const path = tp.path || [];
    if (path.length <= depth) continue;
    if (!ancestorPath.every((sel, i) => path[i]?.nodeId === sel.nodeId)) continue;
    const node = path[depth];
    if (!node?.value) continue;
    seen.set(node.nodeId, node);
  }
  return Array.from(seen.values());
}

export default function LocationStructurePage() {
  // The location structure addresses every document ever filed under it -
  // renaming a tier or deleting a level re-files or orphans real uploads. The
  // API rejects a structure change from anyone but owner/admin, so members see
  // the structure read-only rather than controls that would fail.
  const { isAdmin } = useMe();
  const { project: contextProject } = useProject();
  const projectId = getProjectId(contextProject);

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newTierName, setNewTierName] = useState('');
  // Keyed by depth so each tier's add-value box has its own text, rather than
  // every depth sharing one string.
  const [newValueByDepth, setNewValueByDepth] = useState({});
  const [selectedPath, setSelectedPath] = useState([]);
  const [newPackageName, setNewPackageName] = useState('');
  const [newPackageSupplier, setNewPackageSupplier] = useState('');

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    fetchFullProject(projectId)
      .then(setProject)
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [projectId]);

  const tierDefinitions = project?.tierDefinitions?.length ? project.tierDefinitions : [];
  const tierPaths = useMemo(() => flattenTree(project?.master || [], tierDefinitions), [project, tierDefinitions]);

  // Trim the selection back to whatever is still resolvable after an edit.
  useEffect(() => {
    setSelectedPath((prev) => {
      let valid = [];
      for (let depth = 0; depth < prev.length; depth += 1) {
        const match = optionsAtDepth(tierPaths, depth, valid).find((o) => o.nodeId === prev[depth]?.nodeId);
        if (!match) break;
        valid = [...valid, match];
      }
      return valid.length === prev.length ? prev : valid;
    });
  }, [tierPaths]);

  // Replace local state with the server's copy after each save: the merge can
  // legitimately return something not byte-identical to what was sent, and
  // trusting the optimistic version indefinitely is how the two silently
  // diverge.
  async function persist(next) {
    setProject(next);
    setSaving(true);
    setError('');
    try {
      const saved = await saveProject(next);
      if (saved && Array.isArray(saved.master)) setProject(saved);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function handleAddValue(depth) {
    const name = (newValueByDepth[depth] || '').trim();
    if (!name || !project) return;
    persist(addValueAtPath(project, selectedPath.slice(0, depth).map((p) => p.nodeId), name));
    setNewValueByDepth((prev) => ({ ...prev, [depth]: '' }));
  }

  if (loading) return <Loading text="Loading location structure…" />;

  return (
    <>
      <p className="sub">Define as many tiers as you need (e.g. Block, Level, Zone), then add the values under each.</p>
      {!isAdmin && <p className="muted">Only an owner or admin can change the location structure.</p>}
      <ErrorText>{error}</ErrorText>
      {saving && <Loading text="Saving…" />}

      <Card title="Packages / trades" hint="Who the work is done by — picked first when uploading, separate from the location tiers below.">
        <Chips
          items={(project?.responsibilities || []).map((p) => ({ id: p.id, label: p.supplier ? `${p.name} (${p.supplier})` : p.name }))}
          onRemove={isAdmin ? (id) => persist(removePackage(project, id)) : undefined}
        />
        {isAdmin && (
        <div className="row">
          <input className="input" style={{ flex: 1, minWidth: 180 }} placeholder="Package name (e.g. Roofing)" value={newPackageName} onChange={(e) => setNewPackageName(e.target.value)} />
          <input className="input" style={{ flex: 1, minWidth: 180 }} placeholder="Supplier (optional)" value={newPackageSupplier} onChange={(e) => setNewPackageSupplier(e.target.value)} />
          <Button compact disabled={!newPackageName.trim()} onClick={() => {
            persist(addPackage(project, { name: newPackageName, supplier: newPackageSupplier }));
            setNewPackageName('');
            setNewPackageSupplier('');
          }}>Add package</Button>
        </div>
        )}
      </Card>

      <Card title="Tiers" hint="This order is the order they're picked in when uploading — e.g. Block then Level.">
        <Chips
          items={tierDefinitions.map((t) => ({ id: t.id, label: t.name }))}
          onRemove={isAdmin ? (id) => persist(removeTier(project, id)) : undefined}
        />
        {isAdmin && (
        <div className="row">
          <input className="input" style={{ flex: 1, minWidth: 180 }} placeholder="e.g. Zone" value={newTierName} onChange={(e) => setNewTierName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && newTierName.trim() && (persist(addTier(project, newTierName)), setNewTierName(''))} />
          <Button compact disabled={!newTierName.trim()} onClick={() => { persist(addTier(project, newTierName)); setNewTierName(''); }}>Add tier</Button>
        </div>
        )}
      </Card>

      {tierDefinitions.map((tier, depth) => {
        if (depth > selectedPath.length) return null;
        const options = optionsAtDepth(tierPaths, depth, selectedPath.slice(0, depth));
        const selected = selectedPath[depth];
        return (
          <Card
            key={tier.id}
            title={`${depth + 1}. ${tier.name} values`}
            hint={depth > 0 ? `Under ${selectedPath.slice(0, depth).map((p) => p.value).join(' / ')}` : undefined}
          >
            {options.length ? (
              <div className="pill-wrap" style={{ marginBottom: 10 }}>
                {options.map((o) => (
                  <button
                    key={o.nodeId}
                    type="button"
                    className={`pill${selected?.nodeId === o.nodeId ? ' active' : ''}`}
                    onClick={() => setSelectedPath((prev) => [...prev.slice(0, depth), o])}
                  >
                    {o.value}
                  </button>
                ))}
              </div>
            ) : <p className="muted">No {tier.name.toLowerCase()} values yet.</p>}

            {options.length > 0 && (
              <Chips
                items={options.map((o) => ({ id: o.nodeId, label: o.value }))}
                onRemove={isAdmin ? (nodeId) => {
                  persist(removeValueAtPath(project, [...selectedPath.slice(0, depth).map((p) => p.nodeId), nodeId]));
                  if (selectedPath[depth]?.nodeId === nodeId) setSelectedPath((prev) => prev.slice(0, depth));
                } : undefined}
              />
            )}

            {isAdmin && (
            <div className="row">
              <input
                className="input"
                style={{ flex: 1, minWidth: 180 }}
                placeholder={`Add ${tier.name.toLowerCase()}…`}
                value={newValueByDepth[depth] || ''}
                onChange={(e) => setNewValueByDepth((prev) => ({ ...prev, [depth]: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && handleAddValue(depth)}
              />
              <Button compact disabled={!(newValueByDepth[depth] || '').trim()} onClick={() => handleAddValue(depth)}>Add</Button>
            </div>
            )}
          </Card>
        );
      })}

      {!tierDefinitions.length && <p className="muted">Add a tier above to start building this project's location structure.</p>}
    </>
  );
}
